import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { useCart } from '../../context/CartContext';
import Header from '../../components/ui/Header';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';

const ShoppingCart = () => {
  const navigate = useNavigate();
  const { cartItems, removeFromCart, updateQuantity, getCartTotal, clearCart } = useCart();

  const handleQuantityChange = (productId, newQuantity) => {
    if (newQuantity > 0) {
      updateQuantity(productId, newQuantity);
    }
  };

  const handleRemoveItem = (productId) => {
    if (window.confirm('Remove this item from cart?')) {
      removeFromCart(productId);
    }
  };

  const handleCheckout = () => {
    navigate('/checkout');
  };

  const handleContinueShopping = () => {
    navigate('/product-catalog');
  };

  const subtotal = getCartTotal();
  const shipping = 0; // Free shipping
  const total = subtotal + shipping;

  if (cartItems?.length === 0) {
    return (
      <>
        <Helmet>
          <title>Shopping Cart - INF LABS</title>
          <meta name="description" content="Your shopping cart" />
        </Helmet>

        <div className="min-h-screen bg-[var(--color-background)]">
          <Header />
          
          <main className="pt-[100px] pb-12 px-4 md:px-6 lg:px-8">
            <div className="max-w-[1200px] mx-auto">
              <div className="text-center py-16">
                <div className="inline-flex items-center justify-center w-24 h-24 rounded-full bg-muted/50 mb-6">
                  <Icon name="ShoppingCart" size={48} color="var(--color-muted-foreground)" />
                </div>
                <h2 className="text-3xl font-bold text-foreground mb-3">
                  Your Cart is Empty
                </h2>
                <p className="text-lg text-muted-foreground mb-8">
                  Add some peptides to get started
                </p>
                <Button
                  onClick={handleContinueShopping}
                  variant="default"
                  size="lg"
                  iconName="ArrowRight"
                  iconPosition="right"
                >
                  Browse Products
                </Button>
              </div>
            </div>
          </main>
        </div>
      </>
    );
  }

  return (
    <>
      <Helmet>
        <title>Shopping Cart ({cartItems?.length}) - INF LABS</title>
        <meta name="description" content="Review your cart and proceed to checkout" />
      </Helmet>
      <div className="min-h-screen bg-[var(--color-background)]">
        <Header />
        
        <main className="pt-[100px] pb-12 px-4 md:px-6 lg:px-8">
          <div className="max-w-[1200px] mx-auto">
            <div className="flex items-center justify-between mb-8">
              <h1 className="text-3xl md:text-4xl font-bold text-foreground">
                Shopping Cart
              </h1>
              <Button
                onClick={handleContinueShopping}
                variant="outline"
                iconName="ArrowLeft"
                iconPosition="left"
              >
                Continue Shopping
              </Button>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              {/* Cart Items */}
              <div className="lg:col-span-2 space-y-4">
                {cartItems?.map((item) => (
                  <div
                    key={item?.id}
                    className="p-6 rounded-xl bg-card border border-border hover:border-primary/50 transition-all duration-300"
                  >
                    <div className="flex items-start gap-4">
                      {/* Product Image */}
                      <img
                        src={item?.image}
                        alt={item?.imageAlt || item?.name}
                        className="w-24 h-24 object-cover rounded-lg flex-shrink-0"
                      />

                      {/* Product Info */}
                      <div className="flex-1 min-w-0">
                        <h3 className="text-lg font-bold text-foreground mb-2">
                          {item?.name}
                        </h3>
                        <p className="text-xl font-bold text-primary mb-4">
                          ${item?.price?.toFixed(2)}
                        </p>

                        {/* Quantity Controls */}
                        <div className="flex items-center gap-4">
                          <span className="text-sm text-muted-foreground font-medium">
                            Quantity:
                          </span>
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() => handleQuantityChange(item?.id, item?.quantity - 1)}
                              disabled={item?.quantity <= 1}
                              className="w-8 h-8 rounded-lg bg-muted border border-border flex items-center justify-center transition-all duration-250 hover:bg-primary/20 hover:border-primary disabled:opacity-50 disabled:cursor-not-allowed"
                              aria-label="Decrease quantity"
                            >
                              <Icon name="Minus" size={16} color="var(--color-primary)" />
                            </button>
                            <span className="w-12 text-center text-lg font-bold text-foreground">
                              {item?.quantity}
                            </span>
                            <button
                              onClick={() => handleQuantityChange(item?.id, item?.quantity + 1)}
                              disabled={item?.quantity >= item?.maxQuantity}
                              className="w-8 h-8 rounded-lg bg-muted border border-border flex items-center justify-center transition-all duration-250 hover:bg-primary/20 hover:border-primary disabled:opacity-50 disabled:cursor-not-allowed"
                              aria-label="Increase quantity"
                            >
                              <Icon name="Plus" size={16} color="var(--color-primary)" />
                            </button>
                          </div>
                        </div>
                      </div>

                      {/* Item Total & Remove */}
                      <div className="text-right flex flex-col items-end gap-4">
                        <p className="text-2xl font-bold text-foreground">
                          ${(item?.price * item?.quantity)?.toFixed(2)}
                        </p>
                        <button
                          onClick={() => handleRemoveItem(item?.id)}
                          className="flex items-center gap-2 text-error hover:text-error/80 transition-colors duration-200"
                        >
                          <Icon name="Trash2" size={18} color="currentColor" />
                          <span className="text-sm font-medium">Remove</span>
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Order Summary */}
              <div className="lg:col-span-1">
                <div className="p-6 rounded-xl bg-card border border-border sticky top-[120px]">
                  <h2 className="text-xl font-bold text-foreground mb-6 flex items-center gap-2">
                    <Icon name="Receipt" size={24} color="var(--color-primary)" />
                    Order Summary
                  </h2>

                  <div className="space-y-4 mb-6">
                    <div className="flex justify-between text-foreground">
                      <span>Subtotal ({cartItems?.length} items)</span>
                      <span className="font-semibold">${subtotal?.toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between text-foreground">
                      <span>Shipping</span>
                      <span className="font-semibold text-accent">FREE</span>
                    </div>
                    <div className="pt-4 border-t border-border flex justify-between text-lg font-bold">
                      <span className="text-foreground">Total</span>
                      <span className="text-primary">${total?.toFixed(2)}</span>
                    </div>
                  </div>

                  <Button
                    onClick={handleCheckout}
                    variant="default"
                    size="lg"
                    fullWidth
                    iconName="CreditCard"
                    iconPosition="left"
                    className="mb-4"
                  >
                    Send ${total?.toFixed(2)} Amount
                  </Button>

                  <div className="p-4 rounded-lg bg-accent/10 border border-accent/30">
                    <div className="flex items-start gap-3">
                      <Icon name="Truck" size={20} color="var(--color-accent)" className="flex-shrink-0 mt-1" />
                      <div>
                        <p className="text-sm font-semibold text-accent mb-1">
                          Next Day Shipping
                        </p>
                        <p className="text-xs text-foreground">
                          Orders ship the next business day after payment confirmation
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </main>
      </div>
    </>
  );
};

export default ShoppingCart;